import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Fab,
  LinearProgress,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloudIcon from '@mui/icons-material/Cloud'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import AppToolbar, { ViewMode, SortOption } from './components/Toolbar'
import FileList from './components/FileList'
import DropZone from './components/DropZone'
import FileViewer from './components/FileViewer'
import UploadProgress from './components/UploadProgress'
import SelectionToolbar, { BatchAction } from './components/SelectionToolbar'
import ErrorDisplay from './components/ErrorDisplay'
import { AnalysisPage } from './components/analysis'
import { MultiComparisonPage } from './components/comparison'
import { getAcceptString } from './constants/mediaTypes'
import { contextualErrors, FriendlyError } from './services/errorHandler'
import {
  FileItem,
  listFiles,
  listFilesV2,
  createUpload,
  uploadToPresigned,
  confirmUpload,
  failUpload,
  deleteFile,
  downloadFile
} from './services/api'

export type AppConfig = Window['monkeeTranscription']
type UploadStep = 'idle' | 'creating' | 'uploading' | 'confirming'
type AppView = 'files' | 'analysis' | 'comparison'

export default function App({ config }: { config?: AppConfig }) {
  // State
  const [files, setFiles] = useState<FileItem[]>([])
  const [totalPages, setTotalPages] = useState(0)
  const [totalFiles, setTotalFiles] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [snackbar, setSnackbar] = useState<string | null>(null)
  
  // Retry state para polling com erro
  const [retryCountdown, setRetryCountdown] = useState(0)
  const retryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Upload state
  const [uploadStep, setUploadStep] = useState<UploadStep>('idle')
  const [uploadFileName, setUploadFileName] = useState<string>('')

  // UI state
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [sortOption, setSortOption] = useState<SortOption>('createdAt,desc')
  const [viewerFile, setViewerFile] = useState<FileItem | null>(null)

  // Navigation state
  const [currentView, setCurrentView] = useState<AppView>('files')
  const [analysisFile, setAnalysisFile] = useState<FileItem | null>(null)

  // Selection state (sempre habilitado, mas via checkbox)
  const [selectedFiles, setSelectedFiles] = useState<FileItem[]>([])
  const [comparisonFiles, setComparisonFiles] = useState<FileItem[]>([])

  // Refs
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Computed
  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) return files
    const query = searchQuery.toLowerCase()
    return files.filter(f => f.title.toLowerCase().includes(query))
  }, [files, searchQuery])

  const hasProcessing = useMemo(() => files.some(f => f.status === 'processing'), [files])

  // Load files
  const handleLoad = useCallback(async (page = currentPage, sort = sortOption, silent = false) => {
    if (!silent) setLoading(true)
    
    // NÃO limpa o erro antes da requisição - só limpa após sucesso
    // Limpa apenas o contador para mostrar que está tentando
    if (!silent) {
      setRetryCountdown(0)
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current)
        retryIntervalRef.current = null
      }
    }
    
    try {
      // Usa o novo endpoint V2 baseado em TranscriptionAnalysis
      const data = await listFilesV2({ page: page - 1, size: 24 })
      setFiles(data.files || [])
      setTotalPages(data.totalPages || 0)
      setTotalFiles(data.totalFiles || 0)
      setCurrentPage(page)
      
      // Sucesso: limpa erro e contador
      setError(null)
      setRetryCountdown(0)
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current)
        retryIntervalRef.current = null
      }
    } catch (e: any) {
      const friendlyError = contextualErrors.listFiles(e)
      
      // Atualiza o erro (sempre, para garantir que aparece)
      setError(friendlyError)
      setFiles([]) // Limpa a lista quando há erro
      
      // Inicia contador para próxima tentativa (sempre que há erro)
      if (!retryIntervalRef.current) {
        const RETRY_INTERVAL = 10 // segundos
        setRetryCountdown(RETRY_INTERVAL)
        retryIntervalRef.current = setInterval(() => {
          setRetryCountdown(prev => {
            if (prev <= 1) {
              return RETRY_INTERVAL // Reinicia o contador
            }
            return prev - 1
          })
        }, 1000)
      } else {
        // Se já existe o interval, reinicia o contador
        setRetryCountdown(10)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [currentPage, sortOption])

  // Upload handler
  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList || !fileList[0]) return
    const file = fileList[0]
    let createdId: string | null = null

    setUploadFileName(file.name)

    try {
      setUploadStep('creating')
      const create = await createUpload(file)
      createdId = create.fileId

      setUploadStep('uploading')
      await uploadToPresigned(create.presignedUrl, create.headers, file)

      setUploadStep('confirming')
      await confirmUpload(create.fileId)

      setSnackbar('Upload concluído! A transcrição será processada em instantes.')
      handleLoad(1, sortOption)
    } catch (e: any) {
      const friendlyError = contextualErrors.upload(e)
      setError(friendlyError)
      if (createdId) {
        try {
          await failUpload(createdId)
        } catch (inner) {
          console.warn('Falha ao marcar upload como falho', inner)
        }
      }
    } finally {
      setUploadStep('idle')
      setUploadFileName('')
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  // Actions
  const handleUploadClick = () => fileInputRef.current?.click()

  const handleDelete = async (fileId: string) => {
    if (!window.confirm('Deseja realmente excluir este arquivo?')) return
    try {
      await deleteFile(fileId)
      setSnackbar('Arquivo removido com sucesso')
      handleLoad(currentPage, sortOption)
    } catch (e: any) {
      const friendlyError = contextualErrors.delete(e)
      setError(friendlyError)
    }
  }

  const handleDownload = async (fileId: string) => {
    try {
      const res = await downloadFile(fileId)
      if (res.transcriptionUrl) {
        window.open(res.transcriptionUrl, '_blank')
      }
    } catch (e: any) {
      const friendlyError = contextualErrors.download(e)
      setError(friendlyError)
    }
  }

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    handleLoad(page, sortOption)
  }

  const handleSortChange = (sort: SortOption) => {
    setSortOption(sort)
    handleLoad(1, sort)
  }

  // Abre análise para arquivo transcrito
  const handleOpenAnalysis = (file: FileItem) => {
    if (file.status === 'completed') {
      setAnalysisFile(file)
      setCurrentView('analysis')
    } else {
      setViewerFile(file)
    }
  }

  // Volta para lista de arquivos
  const handleBackToFiles = () => {
    setCurrentView('files')
    setAnalysisFile(null)
    setComparisonFiles([])
    setSelectedFiles([])
  }

  // Selection handlers
  const handleSelectFile = (file: FileItem) => {
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.fileId === file.fileId)
      if (isSelected) {
        return prev.filter(f => f.fileId !== file.fileId)
      }
      return [...prev, file]
    })
  }

  const handleSelectAll = () => {
    setSelectedFiles([...filteredFiles])
  }

  const handleClearSelection = () => {
    setSelectedFiles([])
  }

  const handleBatchAction = async (action: BatchAction) => {
    switch (action) {
      case 'compare':
        // Filtra apenas arquivos completos e abre a comparação
        const completedFiles = selectedFiles.filter(f => f.status === 'completed')
        if (completedFiles.length >= 2 && completedFiles.length <= 4) {
          setComparisonFiles(completedFiles)
          setCurrentView('comparison')
          setSelectedFiles([])
        }
        break

      case 'download':
        // Download em lote (sequencial para evitar muitas requests)
        for (const file of selectedFiles.filter(f => f.status === 'completed')) {
          try {
            await handleDownload(file.fileId)
          } catch (e) {
            console.error('Erro ao baixar', file.title)
          }
        }
        setSnackbar(`Download de ${selectedFiles.length} arquivo(s) iniciado`)
        break

      case 'delete':
        if (window.confirm(`Deseja realmente excluir ${selectedFiles.length} arquivo(s)?`)) {
          let deleted = 0
          for (const file of selectedFiles) {
            try {
              await deleteFile(file.fileId)
              deleted++
            } catch (e) {
              console.error('Erro ao excluir', file.title)
            }
          }
          setSnackbar(`${deleted} arquivo(s) excluído(s) com sucesso`)
          handleLoad(currentPage, sortOption)
          setSelectedFiles([])
        }
        break
    }
  }

  // Effects
  useEffect(() => {
    handleLoad(1, sortOption)
    
    // Cleanup do retry interval ao desmontar
    return () => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current)
        retryIntervalRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }

    // Polling quando há arquivos em processamento OU quando há erro (para retry automático)
    if (hasProcessing || error) {
      const interval = error ? 10000 : 5000 // 10s quando há erro, 5s quando processando
      pollingRef.current = setInterval(() => {
        handleLoad(currentPage, sortOption, true)
      }, interval)
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [hasProcessing, currentPage, sortOption, error])

  const busy = uploadStep !== 'idle'
  
  // Verifica se há erro de conexão (desabilita botões)
  const hasConnectionError = error && (error.type === 'network' || error.type === 'server' || error.type === 'timeout')
  const isDisabled = busy || !!hasConnectionError

  // Se estiver na view de análise, mostra a AnalysisPage
  if (currentView === 'analysis' && analysisFile) {
    // Determina se é vídeo baseado na extensão
    const isVideo = ['MP4', 'MOV', 'AVI', 'MKV', 'WEBM'].includes(analysisFile.type?.toUpperCase() || '')
    
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <CssBaseline />
        <AnalysisPage
          fileId={analysisFile.fileId}
          fileName={analysisFile.title}
          fileType={isVideo ? 'VIDEO' : 'AUDIO'}
          processingTimes={{
            createdAt: analysisFile.createdAt,
            processingStartedAt: analysisFile.processingStartedAt ?? null,
            transcriptionCompletedAt: analysisFile.transcriptionCompletedAt ?? null,
            analysisCompletedAt: analysisFile.analysisCompletedAt ?? null
          }}
          onBack={handleBackToFiles}
        />
      </Box>
    )
  }

  // Se estiver na view de comparação, mostra a MultiComparisonPage
  if (currentView === 'comparison' && comparisonFiles.length >= 2) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <CssBaseline />
        <MultiComparisonPage
          files={comparisonFiles}
          onBack={handleBackToFiles}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <CssBaseline />

      {/* Header */}
      <Box
        component="header"
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <Box
          sx={{
            maxWidth: 1400,
            mx: 'auto',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CloudIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Minhas transcrições
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Ferramenta de Transcrição e Análise de Áudio e Vídeo
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Status de processamento */}
          {hasProcessing && (
            <Chip
              icon={<CircularProgress size={14} sx={{ color: '#ff9800' }} />}
              label="Processando arquivos..."
              size="small"
              sx={{
                bgcolor: '#fff8e1',
                color: '#f57c00',
                border: '1px solid #ffe082',
                height: 36,
                px: 1,
                '& .MuiChip-label': { px: 1 }
              }}
            />
          )}

          {/* Botão de refresh */}
          <Tooltip title="Atualizar lista">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleLoad(currentPage, sortOption)}
                disabled={loading || busy}
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                sx={{ borderRadius: 2 }}
              >
                Atualizar
              </Button>
            </span>
          </Tooltip>

          {/* Botão de upload */}
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={e => handleUpload(e.target.files)}
            accept={getAcceptString()}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleUploadClick}
            disabled={isDisabled}
            sx={{
              borderRadius: 2,
              px: 3,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)'
              }
            }}
          >
            Novo arquivo
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 3 }}>
        <DropZone
          onFilesDropped={handleUpload}
          onInvalidFiles={msg => setSnackbar(msg)}
          disabled={isDisabled}
        >
          <Stack spacing={2}>
            {/* Toolbar */}
            <AppToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              sortOption={sortOption}
              onSortChange={handleSortChange}
              totalFiles={totalFiles}
            />

            {/* Error - Card centralizado quando a lista está vazia por erro */}
            {error && files.length === 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  mt: 4,
                  border: '1px solid #ffcdd2',
                  bgcolor: '#fff5f5',
                  textAlign: 'center',
                  borderRadius: 3
                }}
              >
                {/* Ícone */}
                <Box
                  sx={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    bgcolor: error.type === 'network' ? '#ffebee' : '#fff3e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  {error.type === 'network' ? (
                    <WifiOffIcon sx={{ fontSize: 48, color: '#f44336' }} />
                  ) : (
                    <CloudOffIcon sx={{ fontSize: 48, color: '#ff9800' }} />
                  )}
                </Box>

                {/* Título */}
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
                  {error.title}
                </Typography>

                {/* Mensagem */}
                <Typography variant="body1" sx={{ color: '#666', mb: 2, maxWidth: 500, mx: 'auto' }}>
                  {error.message}
                </Typography>

                {/* Sugestão */}
                {error.suggestion && (
                  <Typography variant="body2" sx={{ color: '#888', mb: 4, maxWidth: 450, mx: 'auto' }}>
                    💡 {error.suggestion}
                  </Typography>
                )}

                {/* Botão de retry */}
                {error.canRetry && (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => handleLoad(currentPage, sortOption)}
                    startIcon={<RefreshIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1rem',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                    }}
                  >
                    Tentar novamente
                  </Button>
                )}

                {/* Contador de retry automático */}
                {retryCountdown > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="body2" sx={{ color: '#888', mb: 1.5 }}>
                      Tentando novamente automaticamente em <strong>{retryCountdown}</strong> segundos...
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={(retryCountdown / 10) * 100}
                      sx={{ 
                        maxWidth: 300, 
                        mx: 'auto',
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#90caf9'
                        }
                      }}
                    />
                  </Box>
                )}
              </Paper>
            )}

            {/* Erro menor quando a lista tem arquivos (operações individuais) */}
            {error && files.length > 0 && (
              <Alert 
                severity={error.type === 'network' || error.type === 'server' || error.type === 'auth' ? 'error' : 'warning'} 
                onClose={() => setError(null)}
                action={
                  error.canRetry ? (
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => { setError(null); handleLoad(currentPage, sortOption); }}
                      startIcon={<RefreshIcon />}
                    >
                      Tentar novamente
                    </Button>
                  ) : undefined
                }
              >
                <AlertTitle>{error.title}</AlertTitle>
                {error.message}
                {error.suggestion && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.9 }}>
                    💡 {error.suggestion}
                  </Typography>
                )}
              </Alert>
            )}

            {/* File list */}
            <FileList
              files={filteredFiles}
              viewMode={viewMode}
              loading={loading}
              hasError={!!hasConnectionError}
              onOpen={handleOpenAnalysis}
              onDelete={handleDelete}
              onDownload={handleDownload}
              onUploadClick={handleUploadClick}
              selectionMode={true}
              selectedFiles={selectedFiles}
              onSelectFile={handleSelectFile}
            />

            {/* Pagination */}
            {totalPages > 1 && !searchQuery && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Stack>
        </DropZone>
      </Box>

      {/* FAB para upload (mobile) */}
      <Fab
        color="primary"
        onClick={handleUploadClick}
        disabled={isDisabled}
        sx={{
          position: 'fixed',
          bottom: selectedFiles.length > 0 ? 120 : 24,
          right: 24,
          display: { xs: 'flex', md: 'none' },
          boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)',
          transition: 'bottom 0.3s ease'
        }}
      >
        <AddIcon />
      </Fab>

      {/* File viewer modal */}
      <FileViewer
        file={viewerFile}
        onClose={() => setViewerFile(null)}
        onDownload={file => handleDownload(file.fileId)}
      />

      {/* Upload progress */}
      <UploadProgress step={uploadStep} fileName={uploadFileName} />

      {/* Selection toolbar */}
      <SelectionToolbar
        selectedFiles={selectedFiles}
        totalFiles={filteredFiles.length}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        onAction={handleBatchAction}
        onClose={() => {
          setSelectedFiles([])
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </Box>
  )
}
